import { ItemType } from "@/types/enums/item-type";

// Package and gift-card lines must stay whole-numbered: a package is issued
// as one bundle, and each gift card gets its own charge item. Everything
// else (product / medication / service / …) can carry a fractional quantity
// such as 1.5 of a compounded cream or half a capsule.
export function requiresIntegerQuantity(opts: {
  itemType?: ItemType;
  isPackageLine?: boolean;
}): boolean {
  return (
    opts.isPackageLine === true ||
    opts.itemType === ItemType.GIFT_CARD ||
    opts.itemType === ItemType.PACKAGE
  );
}

export function parseQuantityInput(
  value: string,
  integerOnly: boolean,
): number {
  if (value.trim() === "") return NaN;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return NaN;
  if (integerOnly && !Number.isInteger(parsed)) return NaN;
  return parsed;
}

export function clampQuantity(
  value: number,
  { min = 0, max }: { min?: number; max?: number } = {},
): number {
  const upperBounded = max == null ? value : Math.min(value, max);
  return Math.max(min, upperBounded);
}

// Sanitize a free-typed decimal string. On keyboards where the decimal
// separator is a comma (many European locales), typing or pasting "12,5"
// would otherwise be stripped to "125" — a 10x error in percent/amount
// discount fields and quantities. Convert a single comma to "." first so
// the value parses to 12.5, then keep only digits and a single dot.
export function sanitizeDecimalString(raw: string): string {
  const normalized = raw.replace(",", ".");
  const digitsAndDot = normalized.replace(/[^0-9.]/g, "");
  const firstDot = digitsAndDot.indexOf(".");
  if (firstDot === -1) return digitsAndDot;
  return (
    digitsAndDot.slice(0, firstDot + 1) +
    digitsAndDot.slice(firstDot + 1).replace(/\./g, "")
  );
}
